import { useQuery } from '@tanstack/react-query';
import { logUnknownErrorResponse } from '@hungryroot/o11y';
import {
  StatusCode,
  getApiV2PricingServiceCustomersCustomerIdProductSubscriptionCustomerConfig,
  getPricingServiceCustomerProductSubscriptionCustomerConfigPath,
  type PricingServiceCustomerProductSubscriptionCustomerConfigParams,
} from '@hungryroot/rest';
import { useNextOrderV3, useOrderDateParam } from '@hungryroot/rest-hooks';

const timeLimit = 15 * 60 * 1000;

function useQueryCustomerProductSubscriptionConfig(
  params: PricingServiceCustomerProductSubscriptionCustomerConfigParams,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: [
      getPricingServiceCustomerProductSubscriptionCustomerConfigPath(params),
    ],
    queryFn: async () => {
      const resp =
        await getApiV2PricingServiceCustomersCustomerIdProductSubscriptionCustomerConfig(
          params
        );

      if (resp.status !== StatusCode.SuccessOK) {
        logUnknownErrorResponse(
          'Failed to get customer product subscription config',
          {
            key: 'useGetCustomerProductSubscriptionConfig',
            error: JSON.stringify(resp.data),
            params,
          }
        );
        throw new Error(JSON.stringify(resp.data, null, 2));
      }

      return resp.data;
    },
    ...options,
    retry: false,
    enabled: options?.enabled,
    gcTime: timeLimit,
    staleTime: timeLimit,
  });
}

export function useGetCustomerProductSubscriptionConfig(
  { containerId }: { containerId?: number } = {},
  options?: { enabled?: boolean }
) {
  const { enabled } = options || {};

  const { order: nextEditableOrder } = useNextOrderV3({}, { enabled });
  const orderDate = useOrderDateParam(nextEditableOrder);

  const customerProductSubscriptionConfigData =
    useQueryCustomerProductSubscriptionConfig(
      {
        containerId,
        origin: nextEditableOrder?.origin,
        orderDate,
      },
      {
        enabled: enabled !== false && orderDate !== undefined,
      }
    );

  return customerProductSubscriptionConfigData;
}
